import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";
import parser from "html-react-parser";
import { Dialog, Transition } from "@headlessui/react";
import { MultiValue, SingleValue } from "react-select";
import Input from "../../../components/input";
import { OptionValue } from "../../../types/component";
import {
  calculateDuration,
  convertDecimalToHourMin,
  convertHourMinToDecimalHours,
  dateFormatter,
  isSelectSingleValue,
  timeFormatter,
  updateDateWithTime,
} from "../../../core/utils";
import Button from "../../../components/button";
import StyledSelect from "../../../components/styled-select";
import {
  branchStateAction,
  BranchResponse,
  getBranchState,
  getOrganizationState,
  getRecurrenceState,
  organizationStateAction,
  OrganizationResponse,
  recurrenceStateAction,
  RecurrenceResponse,
} from "../../../redux/features/other-slice";
import {
  clearNewTaskGroupState,
  createTaskGroupStateAction,
  CreateTaskGroupRequest,
  getNewTaskGroupState,
  getTaskFunctionsForMemberState,
  GetTaskFunctionsForMemberRequest,
  taskFunctionsForMemberStateAction,
  TaskFunctionResponse,
  TaskGroupResponse,
} from "../../../redux/features/task-slice";
import { getUserState } from "../../../redux/features/auth-slice";
import { AppDispatch } from "../../../redux/store";
import {
  deleteReportStateAction,
  getDeleteReportState,
  getUpdateReportState,
  ReportProgress,
  ReportResponse,
  updateReportStateAction,
  UpdateReportRequest,
  reportTagOption,
} from "../../../redux/features/report-slice";
import "../../../types/string-extensions";
import CheckBox from "../../../components/checkbox";
import Switch from "../../../components/switch";

interface UpdateReportDialogProps {
  report: ReportResponse | null;
  setReport: (value: ReportResponse | null) => void;
  onCompleted: () => void;
}

export default function UpdateReportDialog(props: UpdateReportDialogProps) {
  const { report, setReport, onCompleted } = props;

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;
  const organizationState = useSelector(getOrganizationState);
  const brancheState = useSelector(getBranchState);
  const recurrenceState = useSelector(getRecurrenceState);
  const taskFunctionsForMemberState = useSelector(
    getTaskFunctionsForMemberState
  );
  const updateReportState = useSelector(getUpdateReportState);
  const deleteReportState = useSelector(getDeleteReportState);
  const newTaskGroupState = useSelector(getNewTaskGroupState);

  const cancelButtonRef = useRef(null);

  const [selectedOrgId, setSelectOrgId] = useState<string>("");
  const [selectedBranchId, setSelectBranchId] = useState<string | undefined>(
    undefined
  );
  const [taskGroups, setTaskGroups] = useState<TaskGroupResponse[]>([]);
  const [isTempTaskFunction, setTempTaskFunction] = useState<boolean>(false);
  const [isCreateNewTask, setCreateNewTask] = useState<boolean>(false);
  const [isTimeRange, setIsTimeRange] = useState<boolean>(true);

  const [initialValues, setInitialValues] = useState({
    id: "",
    taskFunctionId: "",
    taskGroupId: "",
    newTask: "",
    newTaskDescription: "",
    recurrenceId:
      recurrenceState.data.length > 0 ? recurrenceState.data[0].id : "",
    detail: "",
    startTime: "00:00",
    endTime: "00:00",
    duration: { hour: 0, min: 0 },
    quantity: 0,
    progress: ReportProgress.Completed,
    comments: "",
    notes: "",
    overtime: false,
    tag: null,
  });

  const submitUpdateReport = async (values: UpdateReportRequest) => {
    if (report) {
      await dispatch(
        updateReportStateAction({
          ...values,
          detail: values.detail.trim(),
          comments: values.comments.trim(),
          notes: values.notes.trim(),
          tag:
            values.tag !== null
              ? `${dateFormatter(report.date, "ddMMyy")}-${values.tag}`
              : null,
        })
      );
    }
  };

  const submitDeleteReport = async () => {
    if (report) {
      await dispatch(deleteReportStateAction({ id: report.id }));
    }
  };

  const submitNewTask = async (values: CreateTaskGroupRequest) => {
    await dispatch(createTaskGroupStateAction(values));
  };

  const handleClose = () => {
    setReport(null);
  };

  const fetchRecurrenceList = useCallback(async () => {
    await dispatch(recurrenceStateAction({ teamId: user!.team.id }));
  }, [dispatch, user]);

  const fetchTaskFunctionsForMember = useCallback(
    async (req: GetTaskFunctionsForMemberRequest) => {
      await dispatch(taskFunctionsForMemberStateAction(req));
    },
    [dispatch, report]
  );

  const renderTaskFunction = (id: string) => {
    if (id !== "" && taskFunctionsForMemberState) {
      const index = taskFunctionsForMemberState.data.findIndex(
        (e) => e.id === id
      );
      if (index !== -1) {
        setTaskGroups(taskFunctionsForMemberState.data[index].taskGroups);
        setTempTaskFunction(taskFunctionsForMemberState.data[index].temporary);
        return {
          value: id,
          label: taskFunctionsForMemberState.data[index].title,
        };
      }
    }
    return { value: "", label: "Select..." };
  };

  const renderDuration = (startTime: string, endTime: string) => {
    const duration = calculateDuration(startTime, endTime);
    return (
      <label
        className={`w-1/3 ${
          duration.hour < 0 ? "text-error-main" : "text-text-title"
        } italic text-xs`}
      >
        {`${convertHourMinToDecimalHours(duration.hour, duration.minute)} hour${
          convertHourMinToDecimalHours(duration.hour, duration.minute) > 1
            ? "s"
            : ""
        }`}
      </label>
    );
  };

  useEffect(() => {
    if (report) {
      setInitialValues({
        id: report.id,
        taskFunctionId: report.taskGroup.taskFunction.id,
        taskGroupId: report.taskGroup.id,
        newTask: "",
        newTaskDescription: "",
        recurrenceId:
          recurrenceState.data.length > 0 ? recurrenceState.data[0].id : "",
        detail: report.detail,
        startTime: report.startTime
          ? timeFormatter(new Date(report.startTime), "HH:mm")
          : "00:00",
        endTime: report.endTime
          ? timeFormatter(new Date(report.endTime), "HH:mm")
          : "00:00",
        duration: {
          hour: convertDecimalToHourMin(report.duration).hour,
          min: convertDecimalToHourMin(report.duration).minute,
        },
        quantity: report.quantity,
        progress: report.progress,
        comments: report.comments,
        overtime: report.overtime,
        notes: report.notes,
        tag: (report.tag !== null ? report.tag.split("-")[1] : null) as any,
      });
      setIsTimeRange(report.startTime !== null && report.endTime !== null);
      setSelectOrgId(report.taskGroup.taskFunction.organization.id);
      if (report.taskGroup.branch !== null) {
        setSelectBranchId(report.taskGroup.branch.id);
      } else {
        setSelectBranchId(undefined);
      }
      fetchRecurrenceList();
    }
  }, [report, fetchRecurrenceList]);

  useEffect(() => {
    if (user && report) {
      setTaskGroups([]);
      setCreateNewTask(false);
      setTempTaskFunction(false);
      fetchTaskFunctionsForMember({
        teamId: user.team.id,
        organizationId: selectedOrgId,
        branchId: selectedBranchId,
      });
    }
  }, [
    fetchTaskFunctionsForMember,
    user,
    report,
    selectedOrgId,
    selectedBranchId,
  ]);

  useEffect(() => {
    if (report && user) {
      const fetchInitialData = async () => {
        await dispatch(organizationStateAction());
        // if (user.team.name === "Branch Support") {
        //   await dispatch(branchStateAction());
        // }
      };
      fetchInitialData();
    }
  }, [dispatch, user, report]);

  // Listen for update report
  useEffect(() => {
    if (updateReportState.data && updateReportState.data.success) {
      onCompleted();
      handleClose();
      dispatch(clearNewTaskGroupState());
    }
  }, [updateReportState]);

  // Listen for delete report
  useEffect(() => {
    if (deleteReportState.data && deleteReportState.data.success) {
      onCompleted();
      handleClose();
    }
  }, [deleteReportState]);

  // Listen for create new task
  useEffect(() => {
    if (report && newTaskGroupState.data && newTaskGroupState.data.data) {
      const duration = isTimeRange
        ? calculateDuration(initialValues.startTime, initialValues.endTime)
        : {
            hour: initialValues.duration.hour,
            minute: initialValues.duration.min,
          };
      submitUpdateReport({
        ...initialValues,
        taskGroupId: newTaskGroupState.data.data.id,
        startTime: isTimeRange
          ? updateDateWithTime(report.date, initialValues.startTime)
          : null,
        endTime: isTimeRange
          ? updateDateWithTime(report.date, initialValues.endTime)
          : null,
        duration: convertHourMinToDecimalHours(duration.hour, duration.minute),
        taskFunctionId: undefined,
        newTask: undefined,
        newTaskDescription: undefined,
        recurrenceId: undefined,
      } as UpdateReportRequest);
    }
  }, [newTaskGroupState]);

  return (
    <Transition.Root show={report !== null} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 rounded-lg"
        initialFocus={cancelButtonRef}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-11/12 overflow-y-auto m-auto py-8">
          <div className="flex h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            {report && (
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div className="w-full h-full xs:m-4 relative transform overflow-hidden bg-background-default text-left shadow-xl transition-all rounded-lg">
                  <div className="h-12 px-4 flex items-center justify-between bg-background-paper border-b border-divider">
                    <h3
                      className="text-md font-bold leading-6"
                      id="modal-title"
                    >
                      {`Update Report - ${dateFormatter(
                        report.date,
                        "EEEE dd/MM/yyyy"
                      )}`}
                    </h3>
                  </div>
                  <Formik
                    initialValues={initialValues}
                    validationSchema={Yup.object().shape({
                      taskGroupId: isCreateNewTask
                        ? Yup.string()
                        : Yup.string().required("Task is required"),
                      newTask: isCreateNewTask
                        ? Yup.string().required("New Task is required")
                        : Yup.string(),
                      recurrenceId: isCreateNewTask
                        ? Yup.string().required("Recurrence is required")
                        : Yup.string(),
                      quantity: Yup.number()
                        .min(0, "Quantity cannot be less than 0")
                        .required("Quantity is required"),
                    })}
                    onSubmit={(values, { setSubmitting }) => {
                      setSubmitting(false);
                      if (!isCreateNewTask) {
                        const duration = isTimeRange
                          ? calculateDuration(values.startTime, values.endTime)
                          : {
                              hour: values.duration.hour,
                              minute: values.duration.min,
                            };
                        submitUpdateReport({
                          ...values,
                          startTime: isTimeRange
                            ? updateDateWithTime(report.date, values.startTime)
                            : null,
                          endTime: isTimeRange
                            ? updateDateWithTime(report.date, values.endTime)
                            : null,
                          duration: convertHourMinToDecimalHours(
                            duration.hour,
                            duration.minute
                          ),
                          taskFunctionId: undefined,
                          newTask: undefined,
                          newTaskDescription: undefined,
                          recurrenceId: undefined,
                        } as UpdateReportRequest);
                      } else {
                        setInitialValues(values);
                        submitNewTask({
                          title: values.newTask,
                          description: values.newTaskDescription,
                          taskFunctionId: values.taskFunctionId,
                          recurrenceId: values.recurrenceId,
                          branchId: selectedBranchId,
                        } as CreateTaskGroupRequest);
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      handleChange,
                      handleSubmit,
                      isSubmitting,
                      setValues,
                      dirty,
                    }) => (
                      <form
                        noValidate
                        autoComplete="off"
                        onSubmit={handleSubmit}
                        className="overflow-y-auto"
                        style={{
                          height: "calc(100% - 48px)",
                        }}
                      >
                        <div
                          className="grid grid-cols-2 gap-4 overflow-y-scroll p-4"
                          style={{
                            height: "calc(100% - 64px)",
                          }}
                        >
                          <div className="bg-background-paper border border-divider rounded-lg space-y-4 md:space-y-6">
                            <div className="p-4 w-full text-text-title font-medium border-b border-divider pb-4">
                              Task Structure
                            </div>
                            <div className="space-y-4 md:space-y-6 pb-4 px-4">
                              <div className="flex w-full">
                                <label className="w-1/4 text-xs text-text-title">
                                  Organization
                                </label>
                                <div className="w-3/4">
                                  <StyledSelect
                                    loading={organizationState.loading}
                                    options={organizationState.data.map(
                                      (org: OrganizationResponse) => ({
                                        value: org.id,
                                        label: org.name,
                                      })
                                    )}
                                    isClearable={false}
                                    value={
                                      selectedOrgId !== ""
                                        ? (() => {
                                            const index =
                                              organizationState.data.findIndex(
                                                (e) => e.id === selectedOrgId
                                              );
                                            return index !== -1
                                              ? {
                                                  value: selectedOrgId,
                                                  label:
                                                    organizationState.data[
                                                      index
                                                    ].name,
                                                }
                                              : {
                                                  value: "",
                                                  label: "Select...",
                                                };
                                          })()
                                        : { value: "", label: "Select..." }
                                    }
                                    onChange={(
                                      value:
                                        | SingleValue<OptionValue>
                                        | MultiValue<OptionValue>
                                    ) => {
                                      if (value && isSelectSingleValue(value)) {
                                        setValues({
                                          ...values,
                                          taskFunctionId: "",
                                          taskGroupId: "",
                                        });
                                        setSelectOrgId(value.value as string);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              {/* {user && user.team.name === "Branch Support" && (
                                <div className="flex w-full">
                                  <label className="w-1/4 text-xs text-text-title">
                                    Branch
                                  </label>
                                  <div className="w-3/4">
                                    <StyledSelect
                                      loading={brancheState.loading}
                                      options={brancheState.data.map(
                                        (branch: BranchResponse) => ({
                                          value: branch.id,
                                          label: branch.name,
                                        })
                                      )}
                                      isClearable={false}
                                      value={
                                        selectedBranchId !== undefined
                                          ? (() => {
                                              const index =
                                                brancheState.data.findIndex(
                                                  (e) =>
                                                    e.id === selectedBranchId
                                                );
                                              return index !== -1
                                                ? {
                                                    value: selectedBranchId,
                                                    label:
                                                      brancheState.data[index]
                                                        .name,
                                                  }
                                                : {
                                                    value: "",
                                                    label: "Select...",
                                                  };
                                            })()
                                          : { value: "", label: "Select..." }
                                      }
                                      onChange={(
                                        value:
                                          | SingleValue<OptionValue>
                                          | MultiValue<OptionValue>
                                      ) => {
                                        if (
                                          value &&
                                          isSelectSingleValue(value)
                                        ) {
                                          setValues({
                                            ...values,
                                            taskFunctionId: "",
                                            taskGroupId: "",
                                          });
                                          setSelectBranchId(
                                            value.value as string
                                          );
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                              )} */}
                              <div className="flex w-full">
                                <label className="w-1/4 text-xs text-text-title">
                                  Task Function
                                </label>
                                <div className="w-3/4">
                                  <StyledSelect
                                    loading={
                                      taskFunctionsForMemberState.loading
                                    }
                                    options={taskFunctionsForMemberState.data.map(
                                      (task: TaskFunctionResponse) => ({
                                        value: task.id,
                                        label: task.title,
                                      })
                                    )}
                                    isClearable={false}
                                    value={renderTaskFunction(
                                      values.taskFunctionId
                                    )}
                                    onChange={(
                                      value:
                                        | SingleValue<OptionValue>
                                        | MultiValue<OptionValue>
                                    ) => {
                                      if (value && isSelectSingleValue(value)) {
                                        const id = value.value as string;
                                        setValues({
                                          ...values,
                                          taskFunctionId: id,
                                          taskGroupId: "",
                                        });
                                        const tIndex =
                                          taskFunctionsForMemberState.data.findIndex(
                                            (e) => e.id === id
                                          );
                                        const tmpTask =
                                          tIndex !== -1
                                            ? taskFunctionsForMemberState.data[
                                                tIndex
                                              ].temporary
                                            : false;
                                        setTempTaskFunction(tmpTask);
                                        if (!tmpTask) {
                                          setCreateNewTask(false);
                                        }
                                        setTaskGroups(
                                          tIndex !== -1
                                            ? taskFunctionsForMemberState.data[
                                                tIndex
                                              ].taskGroups
                                            : []
                                        );
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <div
                                className={`flex w-full ${
                                  isCreateNewTask
                                    ? "opacity-30 pointer-events-none"
                                    : "opacity-1 pointer-events-auto"
                                }`}
                              >
                                <label className="w-1/4 text-xs text-text-title">
                                  Task *
                                </label>
                                <div className="w-3/4">
                                  <StyledSelect
                                    options={taskGroups.map(
                                      (task: TaskGroupResponse) => ({
                                        value: task.id,
                                        label: task.title,
                                      })
                                    )}
                                    isClearable={false}
                                    value={
                                      values.taskGroupId !== ""
                                        ? (() => {
                                            const index = taskGroups.findIndex(
                                              (e) => e.id === values.taskGroupId
                                            );
                                            return index !== -1
                                              ? {
                                                  value: values.taskGroupId,
                                                  label:
                                                    taskGroups[index].title,
                                                }
                                              : {
                                                  value: "",
                                                  label: "Select...",
                                                };
                                          })()
                                        : { value: "", label: "Select..." }
                                    }
                                    onChange={(
                                      value:
                                        | SingleValue<OptionValue>
                                        | MultiValue<OptionValue>
                                    ) => {
                                      if (value && isSelectSingleValue(value)) {
                                        setValues({
                                          ...values,
                                          taskGroupId: value.value as string,
                                        });
                                      }
                                    }}
                                  />
                                  {errors.taskGroupId && (
                                    <p className="text-error-main text-xs">
                                      {errors.taskGroupId}
                                    </p>
                                  )}
                                  <label className="text-text-title text-xs">
                                    {parser(
                                      taskGroups.filter(
                                        (e) => e.id === values.taskGroupId
                                      ).length > 0
                                        ? taskGroups.filter(
                                            (e) => e.id === values.taskGroupId
                                          )[0].description
                                        : ""
                                    )}
                                  </label>
                                </div>
                              </div>
                              {isTempTaskFunction && (
                                <label className="inline-flex items-center cursor-pointer">
                                  <CheckBox
                                    checked={isCreateNewTask}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setValues({
                                          ...values,
                                          taskGroupId: "",
                                        });
                                      }
                                      setCreateNewTask(e.target.checked);
                                    }}
                                  />
                                  <span className="ms-3 text-xs text-text-title">
                                    Create a New Task
                                  </span>
                                </label>
                              )}
                              {isCreateNewTask && (
                                <>
                                  <div className="w-full flex items-start">
                                    <label className="w-1/4 text-xs text-text-title">
                                      New Task *
                                    </label>
                                    <Input
                                      type="text"
                                      id="newTask"
                                      value={values.newTask}
                                      onChange={handleChange}
                                      errorText={errors.newTask}
                                      exWrapClassName="w-3/4"
                                    />
                                  </div>
                                  <div className="flex w-full">
                                    <label className="w-1/4 text-xs text-text-title">
                                      Recurrence *
                                    </label>
                                    <div className="w-3/4">
                                      <StyledSelect
                                        loading={recurrenceState.loading}
                                        options={recurrenceState.data.map(
                                          (rec: RecurrenceResponse) => ({
                                            value: rec.id,
                                            label: rec.title,
                                          })
                                        )}
                                        isClearable={false}
                                        value={
                                          values.recurrenceId !== ""
                                            ? (() => {
                                                const index =
                                                  recurrenceState.data.findIndex(
                                                    (e) =>
                                                      e.id ===
                                                      values.recurrenceId
                                                  );
                                                return index !== -1
                                                  ? {
                                                      value:
                                                        values.recurrenceId,
                                                      label:
                                                        recurrenceState.data[
                                                          index
                                                        ].title,
                                                    }
                                                  : {
                                                      value: "",
                                                      label: "Select...",
                                                    };
                                              })()
                                            : { value: "", label: "Select..." }
                                        }
                                        onChange={(
                                          value:
                                            | SingleValue<OptionValue>
                                            | MultiValue<OptionValue>
                                        ) => {
                                          if (
                                            value &&
                                            isSelectSingleValue(value)
                                          ) {
                                            setValues({
                                              ...values,
                                              recurrenceId:
                                                value.value as string,
                                            });
                                          }
                                        }}
                                      />
                                      {errors.recurrenceId && (
                                        <p className="text-error-main text-xs">
                                          {errors.recurrenceId}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="w-full flex items-start">
                                    <label className="w-1/4 text-xs text-text-title">
                                      New Task Description
                                    </label>
                                    <Input
                                      type="text"
                                      inputType="textarea"
                                      id="newTaskDescription"
                                      value={values.newTaskDescription}
                                      onChange={handleChange}
                                      errorText={errors.newTaskDescription}
                                      exWrapClassName="w-3/4"
                                    />
                                  </div>
                                </>
                              )}
                              <div className="w-full flex">
                                <label className="w-1/4 text-xs text-text-title">
                                  Tag
                                </label>
                                <div className="w-3/4">
                                  <StyledSelect
                                    options={reportTagOption.map((e) => ({
                                      value: e.value,
                                      label: e.label,
                                    }))}
                                    value={
                                      values.tag !== null
                                        ? (() => {
                                            const index =
                                              reportTagOption.findIndex(
                                                (e) => e.value === values.tag
                                              );
                                            return index !== -1
                                              ? {
                                                  value: values.tag,
                                                  label:
                                                    reportTagOption[index]
                                                      .label,
                                                }
                                              : {
                                                  value: "",
                                                  label: "Select...",
                                                };
                                          })()
                                        : { value: "", label: "Select..." }
                                    }
                                    onChange={(
                                      value:
                                        | SingleValue<OptionValue>
                                        | MultiValue<OptionValue>
                                    ) => {
                                      if (value && isSelectSingleValue(value)) {
                                        setValues({
                                          ...values,
                                          tag: value.value as any,
                                        });
                                      } else {
                                        setValues({ ...values, tag: null });
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <label className="inline-flex items-center cursor-pointer">
                                <CheckBox
                                  checked={values.overtime}
                                  onChange={(e) => {
                                    setValues({
                                      ...values,
                                      overtime: e.target.checked,
                                    });
                                  }}
                                />
                                <span className="ms-3 text-xs text-text-title">
                                  Overtime
                                </span>
                              </label>
                            </div>
                          </div>
                          <div className="bg-background-paper space-y-4 md:space-y-6 border border-divider rounded-lg">
                            <div className="p-4 w-full text-text-title font-medium border-b border-divider pb-4">
                              Task Details
                            </div>
                            <div className="space-y-4 md:space-y-6 pb-4 px-4">
                              <div className="w-full flex items-start">
                                <label className="w-1/4 text-xs text-text-title">
                                  Detail
                                </label>
                                <Input
                                  type="text"
                                  inputType="textarea"
                                  id="detail"
                                  value={values.detail}
                                  onChange={handleChange}
                                  errorText={errors.detail}
                                  exWrapClassName="w-3/4"
                                />
                              </div>
                              <label className="inline-flex items-center cursor-pointer">
                                <Switch
                                  checked={isTimeRange}
                                  onChange={(e) => {
                                    setIsTimeRange(e.target.checked);
                                  }}
                                />
                                <span className="ms-3 text-xs text-text-title">
                                  Time Range Mode
                                </span>
                              </label>
                              {isTimeRange ? (
                                <div className="w-full flex items-start">
                                  <label className="w-1/4 text-xs text-text-title">
                                    Time Range *
                                  </label>
                                  <div className="w-3/4 space-x-4 flex items-center">
                                    <input
                                      type="time"
                                      id="startTime"
                                      min="00:00"
                                      max="23:00"
                                      value={values.startTime}
                                      required
                                      onChange={handleChange}
                                      className="w-2/5 border leading-none border-divider text-sm rounded-lg text-gray-900 block p-2.5"
                                    />
                                    <input
                                      type="time"
                                      id="endTime"
                                      min="00:00"
                                      max="23:00"
                                      value={values.endTime}
                                      required
                                      onChange={handleChange}
                                      className="w-2/5 border leading-none border-divider text-sm rounded-lg text-gray-900 block p-2.5"
                                    />
                                    <label className="w-1/5 text-text-title italic text-xs">
                                      {renderDuration(
                                        values.startTime,
                                        values.endTime
                                      )}
                                    </label>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full flex items-start">
                                  <label className="w-1/4 text-xs text-text-title">
                                    Duration *
                                  </label>
                                  <div className="w-3/4 flex items-center space-x-2">
                                    <StyledSelect
                                      options={Array.from(
                                        { length: 9 },
                                        (_, index) => index
                                      ).map((hour: number) => ({
                                        value: hour,
                                        label: hour.toString(),
                                      }))}
                                      isClearable={false}
                                      value={{
                                        value: values.duration.hour,
                                        label: `${values.duration.hour} hour${
                                          values.duration.hour > 1 ? "s" : ""
                                        }`,
                                      }}
                                      onChange={(
                                        value:
                                          | SingleValue<OptionValue>
                                          | MultiValue<OptionValue>
                                      ) => {
                                        if (
                                          value &&
                                          isSelectSingleValue(value)
                                        ) {
                                          setValues({
                                            ...values,
                                            duration: {
                                              ...values.duration,
                                              hour: value.value as number,
                                            },
                                          });
                                        }
                                      }}
                                      exClassName="w-2/5"
                                    />
                                    <StyledSelect
                                      options={Array.from(
                                        { length: 60 },
                                        (_, index) => index
                                      ).map((min: number) => ({
                                        value: min,
                                        label: min.toString(),
                                      }))}
                                      isClearable={false}
                                      value={{
                                        value: values.duration.min,
                                        label: `${values.duration.min} minute${
                                          values.duration.min > 1 ? "s" : ""
                                        }`,
                                      }}
                                      onChange={(
                                        value:
                                          | SingleValue<OptionValue>
                                          | MultiValue<OptionValue>
                                      ) => {
                                        if (
                                          value &&
                                          isSelectSingleValue(value)
                                        ) {
                                          setValues({
                                            ...values,
                                            duration: {
                                              ...values.duration,
                                              min: value.value as number,
                                            },
                                          });
                                        }
                                      }}
                                      exClassName="w-2/5"
                                    />
                                    <label className="w-1/5 text-text-title italic text-xs">
                                      {`${convertHourMinToDecimalHours(
                                        values.duration.hour,
                                        values.duration.min
                                      )} hour${
                                        convertHourMinToDecimalHours(
                                          values.duration.hour,
                                          values.duration.min
                                        ) > 1
                                          ? "s"
                                          : ""
                                      }`}
                                    </label>
                                  </div>
                                </div>
                              )}
                              <div className="w-full flex items-start">
                                <label className="w-1/4 text-xs text-text-title">
                                  Quantity *
                                </label>
                                <Input
                                  type="number"
                                  id="quantity"
                                  min={0}
                                  value={values.quantity ?? 0}
                                  onChange={handleChange}
                                  errorText={errors.quantity}
                                  exWrapClassName="w-3/4"
                                />
                              </div>
                              <div className="w-full flex items-start">
                                <label className="w-1/4 text-xs text-text-title">
                                  Progress
                                </label>
                                <div className="w-3/4">
                                  <StyledSelect
                                    options={Object.values(ReportProgress).map(
                                      (progress: ReportProgress) => ({
                                        value: progress,
                                        label: progress.capitalizeWords(),
                                      })
                                    )}
                                    isClearable={false}
                                    value={{
                                      value: values.progress,
                                      label: values.progress.capitalizeWords(),
                                    }}
                                    onChange={(
                                      value:
                                        | SingleValue<OptionValue>
                                        | MultiValue<OptionValue>
                                    ) => {
                                      if (value && isSelectSingleValue(value)) {
                                        setValues({
                                          ...values,
                                          progress:
                                            value.value as ReportProgress,
                                        });
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="w-full flex items-start">
                                <label className="w-1/4 text-xs text-text-title">
                                  Comments
                                </label>
                                <Input
                                  type="text"
                                  inputType="textarea"
                                  id="comments"
                                  rows={2}
                                  value={values.comments}
                                  onChange={handleChange}
                                  errorText={errors.comments}
                                  exWrapClassName="w-3/4"
                                />
                              </div>
                              <div className="w-full flex items-start">
                                <label className="w-1/4 text-xs text-text-title">
                                  Notes
                                </label>
                                <Input
                                  type="text"
                                  inputType="textarea"
                                  id="notes"
                                  rows={2}
                                  value={values.notes}
                                  onChange={handleChange}
                                  errorText={errors.notes}
                                  exWrapClassName="w-3/4"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="h-16 px-4 flex items-center justify-between bg-background-paper border-t border-divider">
                          <Button
                            label="Delete"
                            type="button"
                            variant="outlined"
                            loading={
                              updateReportState.loading ||
                              deleteReportState.loading
                            }
                            onClick={submitDeleteReport}
                            exClassName="text-error-main border-error-light"
                          />
                          <div className="flex items-center justify-end">
                            <Button
                              label="Cancel"
                              type="button"
                              variant="outlined"
                              loading={
                                updateReportState.loading ||
                                newTaskGroupState.loading
                              }
                              onClick={handleClose}
                              exClassName="mr-2"
                            />
                            <Button
                              label="Save"
                              type="submit"
                              loading={
                                updateReportState.loading ||
                                newTaskGroupState.loading
                              }
                              disabled={
                                !dirty ||
                                Array.isArray(errors) ||
                                Object.values(errors).toString() != "" ||
                                (isTimeRange
                                  ? calculateDuration(
                                      values.startTime,
                                      values.endTime
                                    ).hour <= 0 &&
                                    calculateDuration(
                                      values.startTime,
                                      values.endTime
                                    ).minute <= 0
                                  : values.duration.hour <= 0 &&
                                    values.duration.min <= 0)
                              }
                            />
                          </div>
                        </div>
                      </form>
                    )}
                  </Formik>
                </div>
              </Transition.Child>
            )}
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
